import * as XLSX from 'xlsx';

export type FleetManagerFieldKey =
  | 'phoneNumber'
  | 'companyName'
  | 'fullName'
  | 'email'
  | 'gstin'
  | 'serviceRegions'
  | 'fleetSize'
  | 'secondaryPhoneNumber'
  | 'whatsappNumber'
  | 'billingAddress';

export interface FleetManagerFieldDefinition {
  key: FleetManagerFieldKey;
  label: string;
  required: boolean;
  description: string;
  aliases: string[];
}

export type ShipperFieldKey =
  | 'phoneNumber'
  | 'companyName'
  | 'fullName'
  | 'email'
  | 'gstin'
  | 'billingAddress'
  | 'secondaryPhoneNumber'
  | 'whatsappNumber';

export interface ShipperFieldDefinition {
  key: ShipperFieldKey;
  label: string;
  required: boolean;
  description: string;
  aliases: string[];
}

export const FLEET_MANAGER_FIELDS: FleetManagerFieldDefinition[] = [
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    required: true,
    description: '10-digit Indian mobile number (+91 will be prefixed automatically)',
    aliases: [
      'phone',
      'mobile',
      'mobilephone',
      'contact no',
      'contact number',
      'phone no',
      'phone number',
      'cell',
      'primary phone',
      'mobile no',
      'contact',
      'mobile_number',
      'phone_number',
    ],
  },
  {
    key: 'companyName',
    label: 'Company / Transporter Name',
    required: true,
    description: 'Business or transporter firm name',
    aliases: [
      'company name',
      'company',
      'business name',
      'firm name',
      'firm',
      'display name',
      'vendor name',
      'transporter name',
      'transporter',
      'enterprise',
      'organization',
      'agency',
      'company_name',
    ],
  },
  {
    key: 'fullName',
    label: 'Contact Person / Full Name',
    required: false,
    description: 'Name of the fleet owner or representative',
    aliases: [
      'full name',
      'contact name',
      'contact person',
      'name',
      'owner name',
      'manager name',
      'first name',
      'person',
      'full_name',
      'contact_name',
    ],
  },
  {
    key: 'secondaryPhoneNumber',
    label: 'Secondary / Alternate Phone',
    required: false,
    description: 'Alternate 10-digit Indian mobile contact number (optional)',
    aliases: [
      'secondary phone',
      'alternate phone',
      'alt phone',
      'secondary mobile',
      'alt mobile',
      'alternate mobile',
      'other phone',
      'phone 2',
      'mobile 2',
      'secondary contact',
      'alt contact',
      'secondary_phone',
      'alternate_phone',
    ],
  },
  {
    key: 'whatsappNumber',
    label: 'WhatsApp Number',
    required: false,
    description: '10-digit WhatsApp contact number (optional)',
    aliases: [
      'whatsapp',
      'whatsapp number',
      'wa number',
      'wa',
      'whats app',
      'whats app number',
      'whatsapp no',
      'wa no',
      'whatsapp_number',
      'wa_number',
    ],
  },
  {
    key: 'email',
    label: 'Email Address',
    required: false,
    description: 'Business email for notifications (optional)',
    aliases: ['email', 'email id', 'emailid', 'e-mail', 'mail', 'email address', 'email_id'],
  },
  {
    key: 'gstin',
    label: 'GSTIN',
    required: false,
    description: '15-character Indian GST identification number (optional)',
    aliases: ['gstin', 'gst no', 'gst number', 'gst', 'tax id', 'gstin no', 'tax number', 'gst_no'],
  },
  {
    key: 'billingAddress',
    label: 'Billing / Registered Address',
    required: false,
    description: 'Full registered or billing address text (optional)',
    aliases: [
      'billing address',
      'address',
      'office address',
      'registered address',
      'registered office',
      'company address',
      'address line',
      'street address',
      'location',
      'billing_address',
      'office_address',
    ],
  },
  {
    key: 'serviceRegions',
    label: 'Operating States / Service Regions',
    required: false,
    description: 'Comma-separated states where fleet operates (e.g. Maharashtra, Gujarat)',
    aliases: [
      'service regions',
      'operating states',
      'states',
      'lanes',
      'routes',
      'locations',
      'operating regions',
      'preferred states',
      'service states',
      'service_regions',
    ],
  },
  {
    key: 'fleetSize',
    label: 'Fleet Size / Truck Count',
    required: false,
    description: 'Total number of trucks operated (optional integer)',
    aliases: [
      'fleet size',
      'no of trucks',
      'truck count',
      'vehicles',
      'total fleet',
      'fleet',
      'trucks',
      'no of vehicles',
      'vehicle count',
      'fleet_size',
    ],
  },
];

export interface ParsedSpreadsheet {
  workbook: XLSX.WorkBook;
  sheetNames: string[];
  activeSheet: string;
  headers: string[];
  rawRows: Record<string, string>[];
  totalRows: number;
}

/**
 * Extracts headers and row records from a specific worksheet in a workbook.
 */
export function extractSheetData(
  workbook: XLSX.WorkBook,
  sheetName: string,
): { headers: string[]; rawRows: Record<string, string>[] } {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    return { headers: [], rawRows: [] };
  }

  // Parse raw matrix of rows (header: 1)
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    defval: '',
    raw: false,
  });

  if (!matrix || matrix.length === 0) {
    return { headers: [], rawRows: [] };
  }

  // Find the first row that contains text as headers
  let headerRowIndex = 0;
  let rawHeaderRow: unknown[] = [];
  for (let i = 0; i < matrix.length; i++) {
    const row = matrix[i];
    if (Array.isArray(row) && row.some((cell) => String(cell).trim() !== '')) {
      headerRowIndex = i;
      rawHeaderRow = row;
      break;
    }
  }

  const headers: string[] = rawHeaderRow
    .map((cell) => String(cell ?? '').trim())
    .filter((h) => h.length > 0);

  if (headers.length === 0) {
    return { headers: [], rawRows: [] };
  }

  const rawRows: Record<string, string>[] = [];
  for (let r = headerRowIndex + 1; r < matrix.length; r++) {
    const row = matrix[r];
    if (!Array.isArray(row)) continue;

    // Check if the row has any non-empty cell
    const hasData = row.some((cell) => String(cell ?? '').trim() !== '');
    if (!hasData) continue;

    const rowObj: Record<string, string> = {};
    headers.forEach((header, colIdx) => {
      rowObj[header] = String(row[colIdx] ?? '').trim();
    });
    rawRows.push(rowObj);
  }

  return { headers, rawRows };
}

/**
 * Reads a File (.csv, .xlsx, .xls) and returns the parsed workbook, sheet names,
 * active sheet headers, and raw row objects.
 */
export async function parseSpreadsheetFile(
  file: File,
  targetSheet?: string,
): Promise<ParsedSpreadsheet> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetNames = workbook.SheetNames;

  if (sheetNames.length === 0) {
    throw new Error('The uploaded spreadsheet contains no worksheets.');
  }

  const activeSheet = targetSheet && sheetNames.includes(targetSheet) ? targetSheet : sheetNames[0];
  const { headers, rawRows } = extractSheetData(workbook, activeSheet);

  return {
    workbook,
    sheetNames,
    activeSheet,
    headers,
    rawRows,
    totalRows: rawRows.length,
  };
}

/**
 * Normalizes string for fuzzy header comparison (lowercase, strips spaces & symbols).
 */
function cleanHeader(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Auto-detects and suggests mapping between detected sheet headers and Fleet Manager fields.
 */
export function autoMapHeaders(
  detectedHeaders: string[],
): Record<FleetManagerFieldKey, string> {
  const mapping: Record<FleetManagerFieldKey, string> = {
    phoneNumber: '',
    companyName: '',
    fullName: '',
    email: '',
    gstin: '',
    serviceRegions: '',
    fleetSize: '',
    secondaryPhoneNumber: '',
    whatsappNumber: '',
    billingAddress: '',
  };

  const usedHeaders = new Set<string>();

  // Pass 1: Exact matches or exact alias matches
  for (const field of FLEET_MANAGER_FIELDS) {
    const cleanedAliases = field.aliases.map(cleanHeader);
    const cleanedFieldKey = cleanHeader(field.key);
    const cleanedLabel = cleanHeader(field.label);

    for (const header of detectedHeaders) {
      if (usedHeaders.has(header)) continue;
      const ch = cleanHeader(header);

      if (
        ch === cleanedFieldKey ||
        ch === cleanedLabel ||
        cleanedAliases.includes(ch)
      ) {
        mapping[field.key] = header;
        usedHeaders.add(header);
        break;
      }
    }
  }

  // Pass 2: Substring matches for unmapped fields
  for (const field of FLEET_MANAGER_FIELDS) {
    if (mapping[field.key]) continue;
    const cleanedAliases = field.aliases.map(cleanHeader);

    for (const header of detectedHeaders) {
      if (usedHeaders.has(header)) continue;
      const ch = cleanHeader(header);

      const matchesAlias = cleanedAliases.some((alias) => ch.includes(alias) || alias.includes(ch));
      if (matchesAlias) {
        mapping[field.key] = header;
        usedHeaders.add(header);
        break;
      }
    }
  }

  return mapping;
}

export const SHIPPER_FIELDS: ShipperFieldDefinition[] = [
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    required: true,
    description: '10-digit Indian mobile number (+91 will be prefixed automatically)',
    aliases: [
      'phone',
      'mobile',
      'mobilephone',
      'contact no',
      'contact number',
      'phone no',
      'phone number',
      'cell',
      'primary phone',
      'mobile no',
      'contact',
      'mobile_number',
      'phone_number',
    ],
  },
  {
    key: 'companyName',
    label: 'Company / Shipper Name',
    required: true,
    description: 'Business or enterprise firm name',
    aliases: [
      'company name',
      'company',
      'business name',
      'firm name',
      'firm',
      'display name',
      'shipper name',
      'shipper',
      'client name',
      'client',
      'customer name',
      'customer',
      'enterprise',
      'organization',
      'agency',
      'company_name',
    ],
  },
  {
    key: 'fullName',
    label: 'Contact Person / Full Name',
    required: false,
    description: 'Name of the primary contact person or manager',
    aliases: [
      'full name',
      'contact name',
      'contact person',
      'name',
      'poc',
      'representative',
      'owner name',
      'manager name',
      'first name',
      'person',
      'full_name',
      'contact_name',
    ],
  },
  {
    key: 'secondaryPhoneNumber',
    label: 'Secondary / Alternate Phone',
    required: false,
    description: 'Alternate 10-digit Indian mobile contact number (optional)',
    aliases: [
      'secondary phone',
      'alternate phone',
      'alt phone',
      'secondary mobile',
      'alt mobile',
      'alternate mobile',
      'other phone',
      'phone 2',
      'mobile 2',
      'secondary contact',
      'alt contact',
      'secondary_phone',
      'alternate_phone',
    ],
  },
  {
    key: 'whatsappNumber',
    label: 'WhatsApp Number',
    required: false,
    description: '10-digit WhatsApp contact number (optional)',
    aliases: [
      'whatsapp',
      'whatsapp number',
      'wa number',
      'wa',
      'whats app',
      'whats app number',
      'whatsapp no',
      'wa no',
      'whatsapp_number',
      'wa_number',
    ],
  },
  {
    key: 'email',
    label: 'Email Address',
    required: false,
    description: 'Business email for notifications (optional)',
    aliases: ['email', 'email id', 'emailid', 'e-mail', 'mail', 'email address', 'email_id'],
  },
  {
    key: 'gstin',
    label: 'GSTIN',
    required: false,
    description: '15-character Indian GST identification number (optional)',
    aliases: ['gstin', 'gst no', 'gst number', 'gst', 'tax id', 'gstin no', 'tax number', 'gst_no'],
  },
  {
    key: 'billingAddress',
    label: 'Billing / Registered Address',
    required: false,
    description: 'Full registered or billing address text (optional)',
    aliases: [
      'billing address',
      'address',
      'office address',
      'registered address',
      'registered office',
      'company address',
      'address line',
      'street address',
      'location',
      'billing_address',
      'office_address',
    ],
  },
];

/**
 * Auto-detects and suggests mapping between detected sheet headers and Shipper fields.
 */
export function autoMapShipperHeaders(
  detectedHeaders: string[],
): Record<ShipperFieldKey, string> {
  const mapping: Record<ShipperFieldKey, string> = {
    phoneNumber: '',
    companyName: '',
    fullName: '',
    email: '',
    gstin: '',
    billingAddress: '',
    secondaryPhoneNumber: '',
    whatsappNumber: '',
  };

  const usedHeaders = new Set<string>();

  // Pass 1: Exact matches or exact alias matches
  for (const field of SHIPPER_FIELDS) {
    const cleanedAliases = field.aliases.map(cleanHeader);
    const cleanedFieldKey = cleanHeader(field.key);
    const cleanedLabel = cleanHeader(field.label);

    for (const header of detectedHeaders) {
      if (usedHeaders.has(header)) continue;
      const ch = cleanHeader(header);

      if (
        ch === cleanedFieldKey ||
        ch === cleanedLabel ||
        cleanedAliases.includes(ch)
      ) {
        mapping[field.key] = header;
        usedHeaders.add(header);
        break;
      }
    }
  }

  // Pass 2: Substring matches for unmapped fields
  for (const field of SHIPPER_FIELDS) {
    if (mapping[field.key]) continue;
    const cleanedAliases = field.aliases.map(cleanHeader);

    for (const header of detectedHeaders) {
      if (usedHeaders.has(header)) continue;
      const ch = cleanHeader(header);

      const matchesAlias = cleanedAliases.some((alias) => ch.includes(alias) || alias.includes(ch));
      if (matchesAlias) {
        mapping[field.key] = header;
        usedHeaders.add(header);
        break;
      }
    }
  }

  return mapping;
}
